import { Box, Button, Card, Checkbox, Chip, Container, Divider, Drawer, FormControlLabel, IconButton, InputAdornment, Radio, List, ListItem, ListItemButton, ListItemIcon, ListItemText, RadioGroup, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Device, MaintenanceGroup, MaintenanceItem, MaintenanceItemQualifier, MaintenanceMechanic } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxTextField from "src/components/inputs/trux-text-field";
import { Constants } from 'src/constants';
import { TruxDeleteTableCell } from 'src/components/trux-delete-table-cell';
import TruxSimpleSelectField from 'src/components/inputs/trux-simple-select-field';
import TruxSimpleNumberField from 'src/components/inputs/trux-simple-number-field';
import TruxCheckboxField from 'src/components/inputs/trux-checkbox-field';
import { ColoredIcon } from 'src/components/colored-icon';
import { SmallTab } from "src/components/small-tab";
import { Colors } from "src/theme/palette";
import { useResponsive } from "src/hooks/use-responsive";
import Scrollbar from "src/components/scrollbar";

type MaintenanceItemsProps = {
  maintenanceItems?: MaintenanceItem[],
  onMaintenanceItemSelected: (a: MaintenanceItem) => void,
  onMaintenanceItemDeleteSelected: (a: MaintenanceItem) => void,
}

type MaintenanceGroupsProps = {
  maintenanceGroups?: MaintenanceGroup[],
  onMaintenanceGroupSelected: (a: MaintenanceGroup) => void,
  onMaintenanceGroupDeleteSelected: (a: MaintenanceGroup) => void,
  devices?: Device[],
  maintenanceMechanics?: MaintenanceMechanic[],
}

function getPeriodFromType(count: number, type: number) {
  if (type === 1) {
    if (count === 1) {
      return "Day";
    }
    return `${count} Days`;
  }
  if (type === 2) {
    if (count === 1) {
      return "Month";
    }
    return `${count} Months`;
  }
  if (type === 3) {
    if (count === 1) {
      return "Years";
    }
    return `${count} Years`;
  }
  return "";
}

function formatNumberForPreview(number: number) {
  return number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function getQualifierPreview(qualifier: MaintenanceItemQualifier) {
  return <>
    {formatNumberForPreview(qualifier.odometerFrom)}-{formatNumberForPreview(qualifier.odometerTo)} miles every&nbsp;
    {qualifier.maintenanceItemQualifierType === Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_TIME && getPeriodFromType(qualifier.everyCount, qualifier.everyType)}
    {qualifier.maintenanceItemQualifierType === Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_ODOMETER && `${formatNumberForPreview(qualifier.everyCount)}mi`}
  </>
}

const MaintenanceItems = memo(({ maintenanceItems, onMaintenanceItemSelected, onMaintenanceItemDeleteSelected }: MaintenanceItemsProps) => {
  const { sortedList, sortData } = useSort(maintenanceItems);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["name", "description"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Maintenance Items" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />

              <SortableHeader sortKey="name" sortData={sortData} >
                Name
              </SortableHeader>

              <SortableHeader sortKey="description" sortData={sortData} >
                Description
              </SortableHeader>

              <TruxTableCell>
                Qualifier
              </TruxTableCell>

              <SortableHeader sortKey="needsReminder" align="center" sortData={sortData}>
                Create Reminders
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.map((maintenanceItem, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onMaintenanceItemSelected(maintenanceItem)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell align="left">
                <IconButton color="error" onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onMaintenanceItemDeleteSelected(maintenanceItem)
                }}>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </TruxTableCell>
              <TruxTableCell>{maintenanceItem.name}</TruxTableCell>
              <TruxTableCell>{maintenanceItem.description}</TruxTableCell>
              <TruxTableCell>{maintenanceItem.qualifiers.map((qualifier, i) => <div key={i}>
                {getQualifierPreview(qualifier)}
              </div>)}</TruxTableCell>

              <TruxTableCell align="center">
                {maintenanceItem.needsReminder && <ColoredIcon color="success">
                  <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                </ColoredIcon>}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

const MaintenanceGroups = memo(({ maintenanceGroups, onMaintenanceGroupSelected, onMaintenanceGroupDeleteSelected, devices, maintenanceMechanics }: MaintenanceGroupsProps) => {
  
  const { sortedList, sortData } = useSort(maintenanceGroups);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["name", "description"]);

  if (!sortedList) {
    return null;
  }

  const getMechanicDisplay = (mechanicIds: number[]) => {
    const groupMechanics = mechanicIds
      .map(id => maintenanceMechanics?.find(m => m.id === id))
      .filter((m): m is MaintenanceMechanic => !!m);
    
    if (groupMechanics.length === 0) {
      return "All Mechanics";
    }

    const displayMechanics = groupMechanics.slice(0, 3);
    const remainingCount = groupMechanics.length - 3;

    return (
      <>
        {displayMechanics.map((mechanic, idx) => (
          <ListItem key={idx} sx={{ p: 0 }}>
            {mechanic.mechanicName}
          </ListItem>
        ))}
        {remainingCount > 0 && (
          <ListItem sx={{ p: 0, pt: 0.5, fontWeight: 500 }}>
            +{remainingCount} more
          </ListItem>
        )}
      </>
    )
  }

  const getDeviceDisplay = (deviceIds: number[]) => {
    const groupDevices = deviceIds
      .map(id => devices?.find(d => d.id === id))
      .filter((d): d is Device => !!d);
    
    if (groupDevices.length === 0) {
      return "Empty Group";
    }
    
    const displayDevices = groupDevices.slice(0, 3);
    const remainingCount = groupDevices.length - 3;
    
    return (
      <List>
        {displayDevices.map((device, idx) => (
          <ListItem key={idx} sx={{ p: 0, pt: 0.5 }}>
            <Chip size="small" label={device.deviceType} color="success" />
          </ListItem>
        ))}
        {remainingCount > 0 && (
          <ListItem sx={{ p: 0, pt: 0.5, fontWeight: 500 }}>
            +{remainingCount} more
          </ListItem>
        )}
      </List>
    )
  };

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Maintenance Groups" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />

              <SortableHeader sortKey="name" sortData={sortData} >
                Name
              </SortableHeader>

              <SortableHeader sortKey="description" sortData={sortData} >
                Description
              </SortableHeader>

              <SortableHeader sortKey="maintenanceGroupMechanicIds" sortData={sortData} >
                Mechanics
              </SortableHeader>

              <SortableHeader sortKey="maintenanceGroupDeviceIds" sortData={sortData} >
                Applies To
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.map((maintenanceGroup, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onMaintenanceGroupSelected(maintenanceGroup)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell align="left">
                <IconButton color="error" onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onMaintenanceGroupDeleteSelected(maintenanceGroup)
                }}>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </TruxTableCell>
              <TruxTableCell>{maintenanceGroup.name}</TruxTableCell>
              <TruxTableCell>{maintenanceGroup.description}</TruxTableCell>
              <TruxTableCell>{getMechanicDisplay(maintenanceGroup.maintenanceGroupMechanicIds)}</TruxTableCell>
              <TruxTableCell>{getDeviceDisplay(maintenanceGroup.maintenanceGroupDeviceIds)}</TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  maintenanceItems?: MaintenanceItem[],
  maintenanceMechanics?: MaintenanceMechanic[],
  devices?: Device[],
  maintenanceGroups?: MaintenanceGroup[],
}

export default function Page() {
  const settings = useSettingsContext();
  const mdUp = useResponsive('up', 'md');
  const dataLoader = useCallback(() => ({
    maintenanceItems: axios.get(endpoints.maintenanceItems.findAll).then(response => response.data),
    maintenanceMechanics: axios.get(endpoints.maintenanceMechanics.findAll).then(response => response.data),
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
    maintenanceGroups: axios.get(endpoints.maintenanceGroups.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpenMaintenanceItem, setIsOpenMaintenanceItem] = useState(false);
  const [isOpenDeleteMaintenanceItem, setIsOpenDeleteMaintenanceItem] = useState(false);

  const [isOpenMaintenanceGroup, setIsOpenMaintenanceGroup] = useState(false);
  const [isOpenDeleteMaintenanceGroup, setIsOpenDeleteMaintenanceGroup] = useState(false);

  const [itemAppliesTo, setItemAppliesTo] = useState<"all" | "groups" | "devices">("all");

  const selectedMaintenanceItemModel = useModelSavingData<MaintenanceItem>(
    endpoints.maintenanceItems.update,
    endpoints.maintenanceItems.create,
    endpoints.maintenanceItems.delete,
  );
  const selectedMaintenanceGroupModel = useModelSavingData<MaintenanceGroup>(
    endpoints.maintenanceGroups.update,
    endpoints.maintenanceGroups.create,
    endpoints.maintenanceGroups.delete,
  );

  const [selectedDevicesForGroup, setSelectedDevicesForGroup] = useState<Device[]>([]);
  const [selectedGroupsForItem, setSelectedGroupsForItem] = useState<MaintenanceGroup[]>([]);
  const [selectedDevicesForItem, setSelectedDevicesForItem] = useState<Device[]>([]);
  const [groupsSearchText, setGroupsSearchText] = useState("");
  const [devicesSearchText, setDevicesSearchText] = useState("");
  const selectedMaintenanceMechanics: string[] = selectedMaintenanceGroupModel.model?.maintenanceGroupMechanicIds.map(mechanicId => data?.maintenanceMechanics?.find(m => m.id === mechanicId)?.mechanicName).filter(m => m) as string[] | [];

  // items
  const updateMaintenanceItem = () => {
    selectedMaintenanceItemModel.update(model => {
      if (itemAppliesTo === "groups") {
        model.appliesToIds = selectedGroupsForItem.map(group => group.id);
      } else if (itemAppliesTo === "devices") {
        model.appliesToIds = selectedDevicesForItem.map(device => device.id);
      } else {
        model.appliesToIds = [];
      }
    });
    selectedMaintenanceItemModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Maintenance Item");
        const maintenanceItem = response.data as MaintenanceItem;
        updateData("maintenanceItems")(replaceById(data!.maintenanceItems!, maintenanceItem));

        setIsOpenMaintenanceItem(false);
      }
    })
  }

  const deleteMaintenanceItem = useCallback(() => {
    selectedMaintenanceItemModel.destroy().then((response) => {
      if (response) {
        enqueueSnackbar("Successfully deleted the Maintenance Item", { variant: "success" })
        setIsOpenDeleteMaintenanceItem(false);
        updateData("maintenanceItems")(deleteById(data!.maintenanceItems!, selectedMaintenanceItemModel.model!));
      }
    })
  }, [data, selectedMaintenanceItemModel, updateData]);

  const onMaintenanceItemSelected = useCallback((a: MaintenanceItem) => {
    selectedMaintenanceItemModel.set(a);
    setItemAppliesTo(a.appliesToType);
    if (a.appliesToType === "groups") {
      setSelectedGroupsForItem(data?.maintenanceGroups?.filter(group => a.appliesToIds.includes(group.id)) || []);
      setSelectedDevicesForItem([]);
    } else if (a.appliesToType === "devices") {
      setSelectedDevicesForItem(data?.devices?.filter(device => a.appliesToIds.includes(device.id)) || []);
      setSelectedGroupsForItem([]);
    } else {
      setSelectedGroupsForItem([]);
      setSelectedDevicesForItem([]);
    }
    setIsOpenMaintenanceItem(true);
  }, [selectedMaintenanceItemModel, setIsOpenMaintenanceItem, data?.maintenanceGroups, data?.devices]);

  const onMaintenanceItemDeleteSelected = useCallback((a: MaintenanceItem) => {
    selectedMaintenanceItemModel.set(a);
    setIsOpenDeleteMaintenanceItem(true);
  }, [selectedMaintenanceItemModel, setIsOpenDeleteMaintenanceItem]);


  const onNewMaintenanceItemSelected = useCallback(() => {
    selectedMaintenanceItemModel.set({
      id: 0,
      createdAt: undefined,
      deletedAt: undefined,
      organizationId: 0,
      name: "",
      needsReminder: true,
      description: "",
      qualifiers: [
        {
          maintenanceItemQualifierType: 1,

          everyType: 1,
          everyCount: 0,

          odometerFrom: 0,
          odometerTo: 100000,
        }
      ],
      appliesToType: "all",
      appliesToIds: [],
    });
    setItemAppliesTo("all");
    setSelectedGroupsForItem([]);
    setSelectedDevicesForItem([]);
    setIsOpenMaintenanceItem(true);
  }, [selectedMaintenanceItemModel]);


  const newMaintenanceItemButton = <Button
    onClick={onNewMaintenanceItemSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Maintenance Item
  </Button>;

  const addQualifier = () => {
    selectedMaintenanceItemModel.update(model => {
      model.qualifiers.push({
        maintenanceItemQualifierType: Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_TIME,
        everyType: 1,
        odometerFrom: 0,
        odometerTo: 350000,
        everyCount: 28
      })
    })
  }

  // groups
  const updateMaintenanceGroup = () => {
    selectedMaintenanceGroupModel.update(model => {
      model.maintenanceGroupDeviceIds = selectedDevicesForGroup.map(device => device.id);
    });
    selectedMaintenanceGroupModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Maintenance Group");
        const maintenanceGroup = response.data as MaintenanceGroup;
        updateData("maintenanceGroups")(replaceById(data!.maintenanceGroups!, maintenanceGroup));

        setIsOpenMaintenanceGroup(false);
      }
    })
  }

  const deleteMaintenanceGroup = useCallback(() => {
    selectedMaintenanceGroupModel.destroy().then((response) => {
      if (response) {
        enqueueSnackbar("Successfully deleted the Maintenance Group", { variant: "success" })
        setIsOpenDeleteMaintenanceGroup(false);
        updateData("maintenanceGroups")(deleteById(data!.maintenanceGroups!, selectedMaintenanceGroupModel.model!));
      }
    })
  }, [data, selectedMaintenanceGroupModel, updateData]);

  const onMaintenanceGroupSelected = useCallback((a: MaintenanceGroup) => {
    selectedMaintenanceGroupModel.set(a);
    setSelectedDevicesForGroup(data?.devices?.filter(device => a.maintenanceGroupDeviceIds.includes(device.id)) || []);
    setIsOpenMaintenanceGroup(true);
  }, [selectedMaintenanceGroupModel, setIsOpenMaintenanceGroup, data?.devices]);

  const onMaintenanceGroupDeleteSelected = useCallback((a: MaintenanceGroup) => {
    selectedMaintenanceGroupModel.set(a);
    setIsOpenDeleteMaintenanceGroup(true);
  }, [selectedMaintenanceGroupModel, setIsOpenDeleteMaintenanceGroup]);


  const onNewMaintenanceGroupSelected = useCallback(() => {
    selectedMaintenanceGroupModel.set({
      id: 0,
      createdAt: undefined,
      deletedAt: undefined,
      organizationId: 0,
      name: "",
      description: "",
      maintenanceGroupMechanicIds: [],
      maintenanceGroupDeviceIds: [],
    });
    setIsOpenMaintenanceGroup(true);
  }, [selectedMaintenanceGroupModel]);


  const newMaintenanceGroupButton = <Button
    onClick={onNewMaintenanceGroupSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Maintenance Group
  </Button>;

  const [selectedTab, setSelectedTab] = useState<"items"|"groups">("items");

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <Stack direction="column" gap={2}>
          <PageHeader title="Maintenance Settings">
            {selectedTab === "items" && (data && data?.maintenanceItems && data?.maintenanceItems?.length > 0 ? newMaintenanceItemButton : null) }
            {selectedTab === "groups" && (data && data?.maintenanceGroups && data?.maintenanceGroups?.length > 0 ? newMaintenanceGroupButton : null) }
          </PageHeader>

          <Tabs
            value={selectedTab}
            onChange={(_, value) => setSelectedTab(value)}
            sx={{
              mt: -3,
              minHeight: "auto"
            }}
          >
            {['items', 'groups'].map((tab) => (
              <SmallTab
                key={tab}
                iconPosition="end"
                value={tab}
                label={tab}
                sx={{ textTransform: 'capitalize' }}
              />))}
          </Tabs>
          
          <Card sx={
            (selectedTab === "items" && data && data?.maintenanceItems?.length !== 0) || 
            (selectedTab === "groups" && data && data?.maintenanceGroups?.length !== 0) ? { p: 2 } : undefined}
          >
            {selectedTab === "items" && <>
              {data && data?.maintenanceItems?.length === 0 &&
                <NoneFound title="No Maintenance Items Found" message="To add a Maintenance Item click the 'New Maintenance Item' button below.">
                  {newMaintenanceItemButton}
                </NoneFound>
              }

              {data && data?.maintenanceItems?.length !== 0 && <MaintenanceItems maintenanceItems={data.maintenanceItems} onMaintenanceItemSelected={onMaintenanceItemSelected} onMaintenanceItemDeleteSelected={onMaintenanceItemDeleteSelected} />}
              
              {selectedMaintenanceItemModel.model &&
                <SimpleDialog
                  title="Delete Maintenance Item"
                  open={isOpenDeleteMaintenanceItem}
                  onCancel={() => setIsOpenDeleteMaintenanceItem(false)}
                  onSubmit={deleteMaintenanceItem}
                  submitDisabled={selectedMaintenanceItemModel.loading}
                  submitButton="Delete Maintenance Item"
                  submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                  submitButtonColor="error">

                  <DisplayErrors savingData={selectedMaintenanceItemModel} />

                  Are you sure you would like to delete this Maintenance Item?
                </SimpleDialog>
              }

              {selectedMaintenanceItemModel.model && <Drawer
                anchor="right"
                open={isOpenMaintenanceItem}
                onClose={() => {
                  setItemAppliesTo("all");
                  setSelectedGroupsForItem([]);
                  setSelectedDevicesForItem([]);
                  setIsOpenMaintenanceItem(false);
                }}
                PaperProps={{
                  sx: {
                    width: { xs: '100%', sm: 600 },
                  },
                }}
              >
                <Stack sx={{ height: '100%' }}>
                  {/* Header */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Typography variant="h6">
                      {selectedMaintenanceItemModel.isNew ? "New Maintenance Item" : "Update Maintenance Item"}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setItemAppliesTo("all");
                        setSelectedGroupsForItem([]);
                        setSelectedDevicesForItem([]);
                        setGroupsSearchText("");
                        setDevicesSearchText("");
                        setIsOpenMaintenanceItem(false);
                      }}
                    >
                      <Iconify icon="mingcute:close-line" />
                    </IconButton>
                  </Stack>

                  {/* Content */}
                  <Scrollbar sx={{ flex: 1 }}>
                    <Stack direction="column" gap={1} sx={{ p: 2.5 }}>
                      <DisplayErrors savingData={selectedMaintenanceItemModel} />

                      <TruxFormField label="Name">
                        <TruxTextField modelSavingData={selectedMaintenanceItemModel} field="name" />
                      </TruxFormField>

                      <TruxFormField label="Description">
                        <TruxTextField modelSavingData={selectedMaintenanceItemModel} field="description" />
                      </TruxFormField>

                      <TruxFormField label={
                          <Box display="flex" alignItems="center" gap={0.75}>
                            Create Reminder
                            <Tooltip title="Only uncheck for minor things you want checked that should not force the vehicle to be sent back to the shop." arrow>
                              <Iconify width={16} icon="eva:info-outline" sx={{ color: 'text.disabled' }} />
                            </Tooltip>
                          </Box>
                        }>
                        <TruxCheckboxField modelSavingData={selectedMaintenanceItemModel} field="needsReminder" />
                      </TruxFormField>
                      
                      <Divider />

                      <Stack>
                        {selectedMaintenanceItemModel.model.qualifiers.length > 0 && <Box>
                          <TableContainer sx={{ maxHeight: 440 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TruxTableCell>
                                    Type
                                  </TruxTableCell>
                                  <TruxTableCell sx={{textWrap: 'nowrap'}}>
                                    Miles From
                                  </TruxTableCell>
                                  <TruxTableCell>
                                    Miles To
                                  </TruxTableCell>
                                  <TruxTableCell>
                                    Every
                                  </TruxTableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedMaintenanceItemModel.model?.qualifiers?.map((qualifier, index) => <TableRow key={index}>
                                  <TruxTableCell>
                                    <TruxSimpleSelectField value={qualifier.maintenanceItemQualifierType} onChange={val => selectedMaintenanceItemModel.update(() => { qualifier.maintenanceItemQualifierType = val })} >
                                      <MenuItem value={Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_TIME}>Time</MenuItem>
                                      <MenuItem value={Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_ODOMETER}>Odometer</MenuItem>
                                    </TruxSimpleSelectField>
                                  </TruxTableCell>
                                  <TruxTableCell>
                                    <TruxSimpleNumberField value={qualifier.odometerFrom} onChange={d => selectedMaintenanceItemModel.update(() => { qualifier.odometerFrom = d })} />
                                  </TruxTableCell>
                                  <TruxTableCell>
                                    <TruxSimpleNumberField value={qualifier.odometerTo} onChange={d => selectedMaintenanceItemModel.update(() => { qualifier.odometerTo = d })} />
                                  </TruxTableCell>
                                  <TruxTableCell>
                                    {qualifier.maintenanceItemQualifierType === Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_TIME && <Box display="flex" gap={1}>
                                      <Box flex={1}>
                                        <TruxSimpleNumberField value={qualifier.everyCount} onChange={d => selectedMaintenanceItemModel.update(() => { qualifier.everyCount = d })} />
                                      </Box>
                                      <Box minWidth="100px" maxWidth="100px" width="100px">
                                        <TruxSimpleSelectField value={qualifier.everyType} onChange={d => selectedMaintenanceItemModel.update(() => { qualifier.everyType = d })}>
                                          <MenuItem value={1}>Day(s)</MenuItem>
                                          <MenuItem value={2}>Month(s)</MenuItem>
                                          {/* <MenuItem value={3}>Year(s)</MenuItem> */}
                                        </TruxSimpleSelectField>
                                      </Box>
                                    </Box>}
                                    {qualifier.maintenanceItemQualifierType === Constants.MAINTENANCE_ITEM_QUALIFIER_TYPE_ODOMETER && <Box display="flex" gap={1}>
                                      <Box flex={1}>
                                        <TruxSimpleNumberField value={qualifier.everyCount} onChange={d => selectedMaintenanceItemModel.update(() => { qualifier.everyCount = d })} />
                                      </Box>
                                      <Box minWidth="72px" maxWidth="72px" width="72px">
                                        <TextField value="miles" disabled size="small" />
                                      </Box>
                                    </Box>}
                                  </TruxTableCell>
                                  <TruxDeleteTableCell>
                                    <IconButton color="error" onClick={() => {
                                      selectedMaintenanceItemModel.update((model) => {
                                        model.qualifiers.splice(model.qualifiers.indexOf(qualifier), 1);
                                      })
                                    }}>
                                      <Iconify icon="solar:trash-bin-trash-bold" />
                                    </IconButton>
                                  </TruxDeleteTableCell>
                                </TableRow>)}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>}
                        {selectedMaintenanceItemModel.model.qualifiers.length === 0 && 
                        <Box sx={{ color: Colors.red, fontSize: "14px", fontStyle: "italic", p: 1 }}>
                          *Please add at least one qualifier
                        </Box>}
                        <Box sx={{ margin: "10px" }}>
                          <Button size="small" variant="soft" startIcon={<Iconify icon="mingcute:add-line" />} onClick={addQualifier}>
                            Add Qualifier
                          </Button>
                        </Box>
                      </Stack>

                      <Divider />

                      <TruxFormField label="Applies To">
                        <RadioGroup
                          value={itemAppliesTo}
                          onChange={(e) => {
                            const newType = e.target.value as 'all' | 'groups' | 'devices';
                            setItemAppliesTo(newType);
                            selectedMaintenanceItemModel.update(model => { model.appliesToType = newType });
                            if (newType === "all") {
                              setSelectedGroupsForItem([]);
                              setSelectedDevicesForItem([]);
                              setGroupsSearchText("");
                              setDevicesSearchText("");
                            } else if (newType === "groups") {
                              setSelectedDevicesForItem([]);
                              setDevicesSearchText("");
                            } else {
                              setSelectedGroupsForItem([]);
                              setGroupsSearchText("");
                            }
                          }}
                          sx={{ paddingLeft: "2px", display: "flex", flexDirection: "row", gap: 1 }}
                        >
                          <FormControlLabel
                            key="all"
                            value="all"
                            control={<Radio size="small" />}
                            label="All"
                          />
                          <FormControlLabel
                            key="groups"
                            value="groups"
                            control={<Radio size="small" />}
                            label="Groups"
                          />
                          <FormControlLabel
                            key="devices"
                            value="devices"
                            control={<Radio size="small" />}
                            label="Devices"
                          />
                        </RadioGroup>
                      </TruxFormField>
                      
                      {itemAppliesTo === "groups" && data?.maintenanceGroups && (
                        <Box>
                          <Stack spacing={1}>
                            {/* Search field */}
                            {data.maintenanceGroups.length > 3 && <TextField
                              size="small"
                              placeholder="Search groups..."
                              value={groupsSearchText}
                              onChange={(e) => setGroupsSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />}
                            
                            {/* Filtered list */}
                            {data.maintenanceGroups.length === 0 ? (
                              <Box sx={{ color: Colors.red, fontSize: "14px", fontStyle: "italic", p: 1 }}>
                                *No maintenance groups to choose from.
                              </Box>
                            ) : (
                              <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <List dense>
                                  {data.maintenanceGroups
                                    .filter(group => 
                                      groupsSearchText === "" || 
                                      group.name.toLowerCase().includes(groupsSearchText.toLowerCase())
                                    )
                                    .map((group) => {
                                      const isSelected = selectedGroupsForItem.some(g => g.id === group.id);
                                      return (
                                        <ListItem key={group.id} disablePadding>
                                          <ListItemButton
                                            onClick={() => {
                                              if (isSelected) {
                                                setSelectedGroupsForItem(selectedGroupsForItem.filter(g => g.id !== group.id));
                                              } else {
                                                setSelectedGroupsForItem([...selectedGroupsForItem, group]);
                                              }
                                            }}
                                          >
                                            <ListItemIcon>
                                              <Checkbox
                                                checked={isSelected}
                                                size="small"
                                              />
                                            </ListItemIcon>
                                            <ListItemText primary={group.name} />
                                          </ListItemButton>
                                        </ListItem>
                                      );
                                    })}
                                </List>
                              </Box>
                            )}

                            {/* Selected chips */}
                            {selectedGroupsForItem.length > 3 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedGroupsForItem.map((group) => (
                                  <Chip
                                    key={group.id}
                                    label={group.name}
                                    size="small"
                                    onDelete={() => {
                                      setSelectedGroupsForItem(selectedGroupsForItem.filter(g => g.id !== group.id));
                                    }}
                                    sx={{ 
                                      height: "27px", 
                                      backgroundColor: Colors.lightGreen, 
                                      color: Colors.greyText, 
                                      "&:hover": { backgroundColor: Colors.lightGreen, color: Colors.greyText }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )}

                      {itemAppliesTo === "devices" && data?.devices && (
                        <Box>
                          <Stack spacing={1}>
                            {/* Search field */}
                            <TextField
                              size="small"
                              placeholder="Search devices..."
                              value={devicesSearchText}
                              onChange={(e) => setDevicesSearchText(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                            
                            {/* Filtered list */}
                            {data.devices.length === 0 ? (
                              <Box sx={{ color: Colors.red, fontSize: "14px", fontStyle: "italic", p: 1 }}>
                                *No devices to assign to.
                              </Box>
                            ) : (
                              <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <List dense>
                                  {data.devices
                                    .filter(device => 
                                      devicesSearchText === "" || 
                                      device.deviceType.toLowerCase().includes(devicesSearchText.toLowerCase())
                                    )
                                    .map((device) => {
                                      const isSelected = selectedDevicesForItem.some(d => d.id === device.id);
                                      return (
                                        <ListItem key={device.id} disablePadding>
                                          <ListItemButton
                                            onClick={() => {
                                              if (isSelected) {
                                                setSelectedDevicesForItem(selectedDevicesForItem.filter(d => d.id !== device.id));
                                              } else {
                                                setSelectedDevicesForItem([...selectedDevicesForItem, device]);
                                              }
                                            }}
                                          >
                                            <ListItemIcon>
                                              <Checkbox
                                                checked={isSelected}
                                                size="small"
                                              />
                                            </ListItemIcon>
                                            <ListItemText primary={device.deviceType} />
                                          </ListItemButton>
                                        </ListItem>
                                      );
                                    })}
                                </List>
                              </Box>
                            )}

                            {/* Selected chips */}
                            {selectedDevicesForItem.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedDevicesForItem.map((device) => (
                                  <Chip
                                    key={device.id}
                                    label={device.deviceType}
                                    size="small"
                                    onDelete={() => {
                                      setSelectedDevicesForItem(selectedDevicesForItem.filter(d => d.id !== device.id));
                                    }}
                                    sx={{ 
                                      height: "27px", 
                                      backgroundColor: Colors.lightGreen, 
                                      color: Colors.greyText, 
                                      "&:hover": { backgroundColor: Colors.lightGreen, color: Colors.greyText }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Scrollbar>

                  {/* Footer */}
                  <Divider />
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ p: 2.5 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setItemAppliesTo("all");
                        setSelectedGroupsForItem([]);
                        setSelectedDevicesForItem([]);
                        setGroupsSearchText("");
                        setDevicesSearchText("");
                        setIsOpenMaintenanceItem(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color={selectedMaintenanceItemModel.isNew ? "success" : "info"}
                      startIcon={selectedMaintenanceItemModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
                      onClick={updateMaintenanceItem}
                      disabled={selectedMaintenanceItemModel.loading}
                    >
                      {selectedMaintenanceItemModel.isNew ? "New Maintenance Item" : "Update Maintenance Item"}
                    </Button>
                  </Stack>
                </Stack>
              </Drawer>}
            </>} {/* selectedTab === "items" */}

            {selectedTab === "groups" && <>
              {data && data?.maintenanceGroups?.length === 0 &&
                <NoneFound title="No Maintenance Groups Found" message={
                  <>
                    Maintenance Groups help organize vehicles that require the same type of maintenance.
                    <br />
                    To create a group click the &apos;New Maintenance Group&apos; button below.
                  </>
                }>
                  {newMaintenanceGroupButton}
                </NoneFound>
              }

              {data && data?.maintenanceGroups?.length !== 0 && <MaintenanceGroups maintenanceGroups={data.maintenanceGroups} onMaintenanceGroupSelected={onMaintenanceGroupSelected} onMaintenanceGroupDeleteSelected={onMaintenanceGroupDeleteSelected} devices={data.devices} maintenanceMechanics={data.maintenanceMechanics} />}

              {selectedMaintenanceGroupModel.model &&
                <SimpleDialog
                  title="Delete Maintenance Group"
                  open={isOpenDeleteMaintenanceGroup}
                  onCancel={() => setIsOpenDeleteMaintenanceGroup(false)}
                  onSubmit={deleteMaintenanceGroup}
                  submitDisabled={selectedMaintenanceGroupModel.loading}
                  submitButton="Delete Maintenance Group"
                  submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                  submitButtonColor="error">

                  <DisplayErrors savingData={selectedMaintenanceGroupModel} />

                  Are you sure you would like to delete this Maintenance Group?
                </SimpleDialog>}

              {selectedMaintenanceGroupModel.model && <Drawer
                anchor="right"
                open={isOpenMaintenanceGroup}
                onClose={() => {
                  setSelectedDevicesForGroup([]);
                  setDevicesSearchText("");
                  selectedMaintenanceGroupModel.update(model => {
                    model.maintenanceGroupMechanicIds = [];
                  });
                  setIsOpenMaintenanceGroup(false);
                }}
                PaperProps={{
                  sx: {
                    width: { xs: '100%', sm: 600 },
                  },
                }}
              >
                <Stack sx={{ height: '100%' }}>
                  {/* Header */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Typography variant="h6">
                      {selectedMaintenanceGroupModel.isNew ? "New Maintenance Group" : "Update Maintenance Group"}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setSelectedDevicesForGroup([]);
                        selectedMaintenanceGroupModel.update(model => {
                          model.maintenanceGroupMechanicIds = [];
                        });
                        setIsOpenMaintenanceGroup(false);
                      }}
                    >
                      <Iconify icon="mingcute:close-line" />
                    </IconButton>
                  </Stack>

                  {/* Content */}
                  <Scrollbar sx={{ flex: 1 }}>
                    <Stack direction="column" gap={1} sx={{ p: 2.5 }}>
                      <DisplayErrors savingData={selectedMaintenanceGroupModel} />

                      <TruxFormField label="Name">
                        <TruxTextField modelSavingData={selectedMaintenanceGroupModel} field="name" />
                      </TruxFormField>

                      <TruxFormField label="Description">
                        <TruxTextField modelSavingData={selectedMaintenanceGroupModel} field="description" />
                      </TruxFormField>

                      {data?.devices && data?.devices?.length > 0 &&
                        <Box>
                          <Stack spacing={1}>
                            <TruxFormField label="Applies To">
                              {/* Search field */}
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Search devices..."
                                value={devicesSearchText}
                                onChange={(e) => setDevicesSearchText(e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </TruxFormField>
                            
                            {/* Filtered list */}
                            {data.devices.length === 0 ? (
                              <Box sx={{ color: Colors.red, fontSize: "14px", fontStyle: "italic", p: 1 }}>
                                *No devices to assign to.
                              </Box>
                            ) : (
                              <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <List dense>
                                  {data.devices
                                    .filter(device => 
                                      devicesSearchText === "" || 
                                      device.deviceType.toLowerCase().includes(devicesSearchText.toLowerCase())
                                    )
                                    .map((device) => {
                                      const isSelected = selectedDevicesForGroup.some(d => d.id === device.id);
                                      return (
                                        <ListItem key={device.id} disablePadding>
                                          <ListItemButton
                                            onClick={() => {
                                              if (isSelected) {
                                                setSelectedDevicesForGroup(selectedDevicesForGroup.filter(d => d.id !== device.id));
                                              } else {
                                                setSelectedDevicesForGroup([...selectedDevicesForGroup, device]);
                                              }
                                            }}
                                          >
                                            <ListItemIcon>
                                              <Checkbox
                                                checked={isSelected}
                                                size="small"
                                              />
                                            </ListItemIcon>
                                            <ListItemText primary={device.deviceType} />
                                          </ListItemButton>
                                        </ListItem>
                                      );
                                    })}
                                </List>
                              </Box>
                            )}

                            {/* Selected chips */}
                            {selectedDevicesForGroup.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedDevicesForGroup.map((device) => (
                                  <Chip
                                    key={device.id}
                                    label={device.deviceType}
                                    size="small"
                                    onDelete={() => {
                                      setSelectedDevicesForGroup(selectedDevicesForGroup.filter(d => d.id !== device.id));
                                    }}
                                    sx={{ 
                                      height: "27px", 
                                      backgroundColor: Colors.lightGreen, 
                                      color: Colors.greyText, 
                                      "&:hover": { backgroundColor: Colors.lightGreen, color: Colors.greyText }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Stack>
                        </Box>}

                      {data?.maintenanceMechanics && data?.maintenanceMechanics?.length > 0 && <TruxFormField label={
                          <Box display="flex" alignItems="center" gap={0.75}>
                            Mechanics
                            <Tooltip title={<>
                              Mechanics that will be able to work on vehicles in this group.
                              If you want the group to be available to all mechanics, leave this field blank.
                              </>}>
                              <Iconify width={16} icon="eva:info-outline" sx={{ color: 'text.disabled' }} />
                            </Tooltip>
                          </Box>
                        }>
                        <Select
                          sx={{minWidth: "100%"}}
                          multiple
                          placeholder="Select mechanic(s)"
                          value={selectedMaintenanceMechanics}
                          onChange={(e) => {
                            const selected = e.target.value;
                            if (selected.includes("Clear All")) {
                              selectedMaintenanceGroupModel.update(model => {
                                model.maintenanceGroupMechanicIds = [];
                              })
                              return;
                            }
                            const mechanics = data?.maintenanceMechanics?.filter(mechanic => selected.includes(mechanic.mechanicName)).map(mechanic => mechanic.id);

                            selectedMaintenanceGroupModel.update(model => {
                              model.maintenanceGroupMechanicIds = mechanics || [];
                            })
                          }}
                        >
                          <MenuItem value="Clear All">Clear All</MenuItem>
                          {data?.maintenanceMechanics?.map(mechanic => <MenuItem key={mechanic.id} value={mechanic.mechanicName}>{mechanic.mechanicName}</MenuItem>)}
                        </Select>
                      </TruxFormField>}
                    </Stack>
                  </Scrollbar>

                  {/* Footer */}
                  <Divider />
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ p: 2.5 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedDevicesForGroup([]);
                        selectedMaintenanceGroupModel.update(model => {
                          model.maintenanceGroupMechanicIds = [];
                        });
                        setIsOpenMaintenanceGroup(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color={selectedMaintenanceGroupModel.isNew ? "success" : "info"}
                      startIcon={selectedMaintenanceGroupModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
                      onClick={updateMaintenanceGroup}
                      disabled={selectedMaintenanceGroupModel.loading}
                    >
                      {selectedMaintenanceGroupModel.isNew ? "New Maintenance Group" : "Update Maintenance Group"}
                    </Button>
                  </Stack>
                </Stack>
              </Drawer>}
            </>} {/* selectedTab === "groups" */}
          </Card>
        </Stack>
      </DisplayPageData>
    </Container >
  );
}
